import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState('default');
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      toast.error('المتصفح لا يدعم الإشعارات');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        toast.success('تم تفعيل الإشعارات');
        return true;
      } else {
        toast.error('تم رفض الإشعارات');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  const sendNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted') {
      console.log('Notifications not permitted');
      return;
    }

    const notification = new Notification(title, {
      icon: 'https://customer-assets.emergentagent.com/job_smart-mail-bot/artifacts/zsz37awn_1.png',
      badge: 'https://customer-assets.emergentagent.com/job_smart-mail-bot/artifacts/zsz37awn_1.png',
      tag: options.tag || 'nicole-notification',
      ...options,
    });

    notification.onclick = () => {
      window.focus();
      if (options.onClick) {
        options.onClick();
      }
      notification.close();
    };

    setNotifications(prev => [...prev, { title, options, timestamp: new Date() }]);

    return notification;
  }, [permission]);

  const notifyNewEmail = useCallback((email) => {
    sendNotification('Nicole - رسالة جديدة', {
      body: `من: ${email.sender_name || email.sender}\n${email.subject}`,
      tag: `email-${email.id}`,
      onClick: () => {
        window.location.href = `/emails/${email.id}`;
      },
    });
  }, [sendNotification]);

  const notifyApprovalRequired = useCallback((approval) => {
    sendNotification('Nicole - طلب موافقة', {
      body: `${approval.title}\nمن: ${approval.requested_by_name}`,
      tag: `approval-${approval.id}`,
      onClick: () => {
        window.location.href = `/approvals`;
      },
    });
  }, [sendNotification]);

  const notifyQuoteReceived = useCallback((quote) => {
    sendNotification('Nicole - عرض سعر جديد', {
      body: `تم استلام عرض سعر من ${quote.supplier_name}`,
      tag: `quote-${quote.id}`,
      onClick: () => {
        window.location.href = `/quotes`;
      },
    });
  }, [sendNotification]);

  return (
    <NotificationContext.Provider
      value={{
        permission,
        requestPermission,
        sendNotification,
        notifyNewEmail,
        notifyApprovalRequired,
        notifyQuoteReceived,
        notifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
